import { matchRoutes, type RouteObject } from 'react-router-dom';
import { isNormalAuthAvailable } from 'api/authHeaders';
import { paths } from 'url-registry';
import { loadWarmupModule } from 'utils/chunkReload';

import { appRouteMountEntries, type RouteMountEntry } from './routeMounts';
import type { ChunkLoader, Mount, PageMount } from './types';

/** The part of `window.location` the warm-up reads. */
export type WarmupLocation = Pick<Location, 'pathname' | 'search'>;

// The mount rides on the route object so the match hands it straight back.
type PreloadRoute = RouteObject & { mount: Mount };

// The page the router renders once verify settles, and the search it renders
// with.
type PreloadPage = { mount: PageMount; search: string };

// The mount the router renders at `pathname`. matchRoutes ranks candidates the
// way the router does (static segments beat params).
function matchMount(
  pathname: string,
  entries: readonly RouteMountEntry[]
): Mount | null {
  const routes: PreloadRoute[] = entries.map((entry) => ({
    path: entry.path,
    mount: entry.mount,
  }));
  const matches = matchRoutes(routes, pathname);
  return matches?.[matches.length - 1]?.route.mount ?? null;
}

/**
 * Pure: whether `pathname` renders a page behind ProtectedRoute, the only guard
 * that renders from the cached session. Public, share and auth pages wait for
 * verify, so their requests never run on an unverified cached session.
 */
export function isProtectedPagePath(
  pathname: string,
  entries: readonly RouteMountEntry[] = appRouteMountEntries
): boolean {
  const mount = matchMount(pathname, entries);
  return mount?.kind === 'page' && mount.guard === 'protected';
}

/**
 * The page the router will render for `location`, or null when nothing should
 * be preloaded.
 *
 * Matching ranks candidates the way the router does, so /notes/google resolves
 * to the Google note page rather than the /notes/:note_id live note that
 * precedes it in registry order.
 *
 * `hasToken` stands in for the verify result the guards will act on:
 * - a protected page is worth preloading only with a token; without one the
 *   visitor is about to be sent to /login and the chunk would be wasted.
 * - the login page redirects a token holder to HOME, so HOME is preloaded instead.
 * - every other guard renders its page whichever way verify goes.
 */
function resolvePreloadPage(
  location: WarmupLocation,
  entries: readonly RouteMountEntry[],
  hasToken: boolean
): PreloadPage | null {
  const mount = matchMount(location.pathname, entries);
  if (!mount || mount.kind !== 'page') {
    return null;
  }
  switch (mount.guard) {
    case 'protected':
      return hasToken ? { mount, search: location.search } : null;
    case 'login': {
      if (!hasToken) {
        return { mount, search: location.search };
      }
      // LoginRoute redirects to HOME without the search.
      const home = entries.find((entry) => entry.path === paths.HOME)?.mount;
      return home?.kind === 'page' ? { mount: home, search: '' } : null;
    }
    case 'public':
    case 'share':
    case 'submittal':
    case 'submittalShare':
    case 'restricted':
      return { mount, search: location.search };
  }
}

/** The chunks worth warming for a location, in the order they are fetched. */
export type RouteWarmup = {
  /**
   * Starts the page's first API request when there is no session
   * (PageMount.requestWithoutSession), else null.
   */
  request: (() => void) | null;
  /** The chunk of the page the router will render (resolvePreloadPage). */
  page: PageMount['load'];
  /** The chunks the page lazy-loads as soon as it renders (PageMount.warm). */
  afterPage: readonly ChunkLoader[];
};

/**
 * Pure: the chunks worth warming for `location`, or null when nothing should
 * be preloaded (see resolvePreloadPage for the rules). Never invokes them.
 */
export function resolveRouteWarmup(
  location: WarmupLocation,
  entries: readonly RouteMountEntry[],
  hasToken: boolean
): RouteWarmup | null {
  const page = resolvePreloadPage(location, entries, hasToken);
  if (!page) {
    return null;
  }
  const sendRequest = page.mount.requestWithoutSession;
  return {
    request: sendRequest && !hasToken ? () => sendRequest(page.search) : null,
    page: page.mount.load,
    afterPage: page.mount.warm?.(new URLSearchParams(page.search)) ?? [],
  };
}

/**
 * Starts the downloads the first route needs from the recruiter entry, while
 * the app chunk is still downloading: for a visitor without a session, the
 * page's first API request (PageMount.requestWithoutSession), then English
 * `common`, which every page waits on (createAppRouter), then the page chunk,
 * then, once the page chunk and its imports have loaded, the chunks the page
 * lazy-loads on render. The order keeps the small, blocking `common` from
 * queueing behind a large chunk, and the tab chunks (CampaignDetailsPage alone
 * is several times the page) from taking bandwidth the page itself still
 * needs. Nothing renders or registers
 * here: the router, the tab panels and ensureEnglishCommon call the same
 * loaders later and find the import in flight or loaded.
 *
 * Every import runs through loadWarmupModule, so a failed fetch (a stale
 * build's chunk hash after a deploy) neither reloads the page nor becomes an
 * unhandled rejection: a tab the page never shows, or a page verify redirects
 * away from, must not reload it. Whatever does render the chunk imports it
 * again, and that caller owns the reload recovery.
 */
export function startRecruiterRouteWarmup(
  location: WarmupLocation,
  entries: readonly RouteMountEntry[] = appRouteMountEntries
): void {
  const warmup = resolveRouteWarmup(location, entries, isNormalAuthAvailable());
  warmup?.request?.();
  // The file ensureEnglishCommon imports, so both share one module and fetch.
  // swallow-ok: Only the warmup failed; route loaders await ensureEnglishCommon, which imports it again and rejects into the route error boundary.
  void loadWarmupModule(() => import('locales/en/common.json')).catch(() => {});
  if (!warmup) {
    return;
  }
  const warmTabs = (): void => {
    for (const load of warmup.afterPage) {
      // swallow-ok: Only tab warmup failed; the page imports the same chunk through lazyWithReload or React.lazy, whose error boundary owns the authoritative load and retry.
      void loadWarmupModule(load).catch(() => {});
    }
  };
  // swallow-ok: Only route warmup failed; the same loader runs inside lazyWithReload and its error boundary owns the authoritative load and retry. The tabs are not warmed after a failure, since the page will not render them.
  void loadWarmupModule(warmup.page)
    .then(warmTabs)
    .catch(() => {});
}
